import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { CarbonCredit } from 'src/app/shared/interfaces/carbon-credit';
import { ProjectTable } from 'src/app/components/project-table/project-table.component';

import { CarbonFilter } from '../characteristic-consult/interfaces/carbon-filter';
import { countRegisterConfirmationFilters } from './shared/register-confirmation-filter.utils';
import { ClickTypeEnum, RegisterConfirmationService } from './service/register-confirmation.service';

interface RegisterConfirmationToastState {
  open: boolean;
  variant: 'success' | 'danger';
  label: string;
  description: string;
}

@Component({
  standalone: false,
  selector: 'app-register-confirmation',
  templateUrl: './register-confirmation.component.html',
  styleUrls: ['./register-confirmation.component.scss'],
})
export class RegisterConfirmationComponent extends ProjectTable<CarbonCredit> implements OnInit, AfterViewInit, OnDestroy {
  readonly ClickTypeEnum = ClickTypeEnum;

  searchCarbonCreditCode = '';
  activeFiltersCount = 0;
  showFilterUpdateNotice = false;
  showFilterDrawer = false;
  isApproveModalOpen = false;
  isRejectModalOpen = false;
  showDetailsDrawer = false;
  activeToast: RegisterConfirmationToastState | null = null;
  isErrorModalOpen = false;
  errorModalMessage = 'Não foi possível concluir a operação. Tente novamente.';

  selectedCreditCodeForDetails: string | null = null;

  private selectedCreditForApproval: CarbonCredit | null = null;
  private selectedCreditForReject: CarbonCredit | null = null;
  private approvalSubscription?: Subscription;
  private rejectSubscription?: Subscription;
  private loadSubscription?: Subscription;
  private filterEventsEnabled = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly registerConfirmationService: RegisterConfirmationService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.registerConfirmationService.filters
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateActiveFiltersState());

    this.loadPendingApproval();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.filterEventsEnabled = true;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.loadSubscription?.unsubscribe();
    this.approvalSubscription?.unsubscribe();
    this.rejectSubscription?.unsubscribe();
  }

  get activeFiltersLabel(): string {
    return this.activeFiltersCount === 1
      ? '1 filtro realizado'
      : `${this.activeFiltersCount} filtros realizados`;
  }

  get filterButtonAriaLabel(): string {
    return this.activeFiltersCount > 0
      ? `Filtrar, ${this.activeFiltersLabel}`
      : 'Filtrar';
  }

  get creditsFoundLabel(): string {
    const count = this.totalItens ?? 0;
    return count === 1
      ? '1 resultado encontrado para o filtro selecionado'
      : `${count} resultados encontrados para o filtro selecionado`;
  }

  get recordsPerPageLabel(): string {
    const pageSize = this.pagination.size ?? 10;
    return pageSize === 1
      ? 'Mostrando 1 registro por página'
      : `Mostrando ${pageSize} registros por página`;
  }

  onFilterTable(): void {
    if (!this.filterEventsEnabled) {
      return;
    }

    this.pagination.page = 1;
    this.updateActiveFiltersState();
    this.loadPendingApproval();
  }

  openFilterDrawer(): void {
    this.showFilterDrawer = true;
  }

  onFilterDrawerClosed(): void {
    this.showFilterDrawer = false;
  }

  onDrawerFiltersApplied(): void {
    this.pagination.page = 1;
    this.updateActiveFiltersState();
    this.loadPendingApproval();
  }

  onFilterNoticeDismiss(): void {
    this.showFilterUpdateNotice = false;
  }

  onFilterNoticeUndo(): void {
    this.registerConfirmationService.setDrawerFilters({} as CarbonFilter);
    this.searchCarbonCreditCode = '';
    this.showFilterUpdateNotice = false;
    this.updateActiveFiltersState();
    this.pagination.page = 1;
    this.loadPendingApproval();
  }

  onApproveModalClosed(confirmed: boolean): void {
    this.isApproveModalOpen = false;

    if (!confirmed || !this.selectedCreditForApproval) {
      this.selectedCreditForApproval = null;
      return;
    }

    const credit = this.selectedCreditForApproval;
    this.selectedCreditForApproval = null;

    this.approvalSubscription?.unsubscribe();
    this.approvalSubscription = this.registerConfirmationService
      .carbonCreditApproval(
        [{
          carbonCreditCode: credit.carbonCreditCode,
          statusCode: 'APPROVED',
        }],
        { showToast: false },
      )
      .subscribe({
        next: () => {
          this.showOperationToast(credit, 'approve');
          this.onPageChange(this.pagination.page ?? 1);
        },
        error: () => {
          this.openErrorModal('Não foi possível aprovar o crédito de carbono.');
        },
      });
  }

  onRejectModalClosed(confirmed: boolean): void {
    this.isRejectModalOpen = false;

    if (!confirmed || !this.selectedCreditForReject) {
      this.selectedCreditForReject = null;
      return;
    }

    const credit = this.selectedCreditForReject;
    this.selectedCreditForReject = null;

    this.rejectSubscription?.unsubscribe();
    this.rejectSubscription = this.registerConfirmationService
      .carbonCreditApproval(
        [{
          carbonCreditCode: credit.carbonCreditCode,
          statusCode: 'REJECTED',
        }],
        { showToast: false },
      )
      .subscribe({
        next: () => {
          this.showOperationToast(credit, 'reject');
          this.onPageChange(this.pagination.page ?? 1);
        },
        error: () => {
          this.openErrorModal('Não foi possível rejeitar o crédito de carbono.');
        },
      });
  }

  onToastDismiss(): void {
    this.activeToast = null;
  }

  onDetailsDrawerClosed(): void {
    this.showDetailsDrawer = false;
    this.selectedCreditCodeForDetails = null;
  }

  onDetailsLoadFailed(): void {
    this.showDetailsDrawer = false;
    this.selectedCreditCodeForDetails = null;
    this.openErrorModal('Não foi possível carregar os detalhes do crédito.');
  }

  onErrorModalClosed(): void {
    this.isErrorModalOpen = false;
  }

  onPaginationChange(event: Event): void {
    const customEvent = event as CustomEvent<{ current: number; previous: number }>;
    const nextPage = customEvent.detail?.current;

    if (nextPage && nextPage !== this.pagination.page) {
      this.onPageChange(nextPage);
    }
  }

  onPageChange(currentPage: number): void {
    this.pagination.page = currentPage;
    this.loadPendingApproval();
  }

  onRowAction(tipo: ClickTypeEnum, row: CarbonCredit): void {
    switch (tipo) {
      case ClickTypeEnum.APROVAR:
        this.onClickApprove(row);
        break;
      case ClickTypeEnum.REJEITAR:
        this.onClickReject(row);
        break;
      case ClickTypeEnum.DETALHES:
        this.onClickDetails(row);
        break;
    }
  }

  trackByCarbonCreditCode(_index: number, credit: CarbonCredit): string {
    return credit.carbonCreditCode ?? `${_index}`;
  }

  getRowApproveLabel(credit: CarbonCredit): string {
    return `Aprovar crédito ${credit.carbonCreditCode || ''}`.trim();
  }

  getRowRejectLabel(credit: CarbonCredit): string {
    return `Reprovar crédito ${credit.carbonCreditCode || ''}`.trim();
  }

  getRowDetailsLabel(credit: CarbonCredit): string {
    return `Visualizar detalhes do crédito ${credit.carbonCreditCode || ''}`.trim();
  }

  private loadPendingApproval(): void {
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.registerConfirmationService
      .getPendingApproval(this.buildCurrentFilter(), this.pagination)
      .subscribe(response => {
        this.itens = response.results ?? [];
        this.totalItens = response.total ?? 0;
        this.totalPages = Math.ceil(this.totalItens / (this.pagination.size ?? 10));
      });
  }

  private buildCurrentFilter(): CarbonFilter {
    const drawerFilters = this.registerConfirmationService.getDrawerFilters();
    const toolbarCarbonCreditCode = this.searchCarbonCreditCode.trim();

    return {
      ...drawerFilters,
      carbonCreditCode: toolbarCarbonCreditCode || drawerFilters.carbonCreditCode || undefined,
    };
  }

  private updateActiveFiltersState(): void {
    this.activeFiltersCount = countRegisterConfirmationFilters(
      this.registerConfirmationService.getDrawerFilters(),
      this.searchCarbonCreditCode,
    );
    this.showFilterUpdateNotice = this.activeFiltersCount > 0;
  }

  private onClickApprove(row: CarbonCredit): void {
    this.selectedCreditForApproval = row;
    this.isApproveModalOpen = true;
  }

  private onClickReject(row: CarbonCredit): void {
    this.selectedCreditForReject = row;
    this.isRejectModalOpen = true;
  }

  private onClickDetails(credit: CarbonCredit): void {
    this.selectedCreditCodeForDetails = credit.carbonCreditCode ?? null;
    this.showDetailsDrawer = true;
  }

  private showOperationToast(credit: CarbonCredit, action: 'approve' | 'reject'): void {
    this.activeToast = {
      open: true,
      variant: action === 'approve' ? 'success' : 'danger',
      label: action === 'approve'
        ? 'Créditos de Carbono aprovado com sucesso!'
        : 'Créditos de Carbono rejeitado!',
      description: this.buildOperationToastDescription(credit),
    };
  }

  private buildOperationToastDescription(credit: CarbonCredit): string {
    const carbonCode = credit.carbonCreditCode?.trim() || '-';
    const holderCode = credit.holderCode?.trim() || '-';

    return `Cód. Carbono ${carbonCode} Cód. Conta Detentor ${holderCode}`;
  }

  private openErrorModal(message: string): void {
    this.errorModalMessage = message;
    this.isErrorModalOpen = true;
  }
}
